import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'reactstrap';
import { FaPlus, FaEdit, FaTrash, FaGripLines } from 'react-icons/fa';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useMutation } from 'react-apollo';
import { toast } from 'react-toastify';

import AddEditCategoryModal from './AddEditCategoryModal';
import { REORDER_SPECIALTY_CATEGORIES } from '../queries';

const SpecialtyCategoriesTab = ({ mappings }) => {
  const categories = [
    ...new Map(mappings.map(item => [item.specialty, item])).values()
  ].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

  const [categoryList, setCategoryList] = useState(categories);

  const [modalOpen, setModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const toggleModal = () => setModalOpen(!modalOpen);

  useEffect(() => {
    setCategoryList(categories);
  }, [mappings]);

  const [reorderCategories] = useMutation(REORDER_SPECIALTY_CATEGORIES);

  const openAdd = () => {
    setIsEdit(false);
    setSelectedCategory(null);
    setModalOpen(true);
  };

  const openEdit = category => {
    setIsEdit(true);
    setSelectedCategory(category);
    setModalOpen(true);
  };

  const onDragEnd = async result => {
    if (!result.destination) return;

    const items = Array.from(categoryList);
    const [moved] = items.splice(result.source.index, 1);

    items.splice(result.destination.index, 0, moved);

    const updated = items.map((item, index) => ({
      ...item,
      displayOrder: index + 1
    }));

    setCategoryList(updated);

    try {
      const payload = updated.map(cat => ({
        specialty: cat.specialty,
        displayOrder: cat.displayOrder
      }));

      await reorderCategories({
        variables: { input: payload }
      });

      toast.success('Display order updated successfully', { autoClose: 3000 });
    } catch (err) {
      toast.error('Failed to update display order', { autoClose: 3000 });
    }
  };

  return (
    <div className="mapping-wrapper">
      <div className="mapping-header-row">
        <div className="mapping-table">
          <div className="category-header">
            <div className="cell header-cell">Display Order</div>
            <div className="cell header-cell">Category Name</div>
            <div className="cell header-cell">Icon</div>
            <div className="cell header-cell">Actions</div>
          </div>
        </div>

        <Button className="add-mapping-btn" onClick={openAdd}>
          <FaPlus className="add-icon" />
          Add New Category
        </Button>
      </div>

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="categories">
          {provided => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {categoryList.map((cat, index) => (
                <Draggable
                  key={cat.specialty}
                  draggableId={cat.specialty}
                  index={index}
                >
                  {provided => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className="category-row"
                    >
                      <div
                        className="cell display-order"
                        {...provided.dragHandleProps}
                      >
                        <FaGripLines className="drag-icon" />
                        {index + 1}
                      </div>

                      <div className="cell">{cat.specialty}</div>

                      <div className="cell icon-cell">
                        {cat.iconLink ? (
                          <img
                            src={cat.iconLink}
                            alt={cat.specialty}
                            className="category-icon"
                          />
                        ) : (
                          <div className="icon-placeholder" />
                        )}
                      </div>

                      <div className="cell actions">
                        <span className="edit" onClick={() => openEdit(cat)}>
                          <FaEdit />
                          Edit
                        </span>

                        <span className="delete">
                          <FaTrash />
                          Delete
                        </span>
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}

              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <AddEditCategoryModal
        isOpen={modalOpen}
        toggle={toggleModal}
        isEdit={isEdit}
        category={selectedCategory}
      />
    </div>
  );
};

SpecialtyCategoriesTab.propTypes = {
  mappings: PropTypes.array
};

export default SpecialtyCategoriesTab;
